/**
 * EventDetail — one AI event, read in a glance and WITHOUT scrolling.
 *
 * The old detail panel was a narrow column: snapshot on top, then every column of the table
 * repeated underneath as an undifferentiated label/value grid. Two things were wrong with it.
 * The image — the reason the record was opened — arrived small, and the three facts an operator
 * actually acts on (WHEN, WHO, IN or OUT) were mixed in with track ids and confidences, so
 * finding them meant reading the whole grid and scrolling past it.
 *
 * So: snapshots take one side at full width, and the facts split in two. `facts` are the few
 * that answer "what happened" and get tiles big enough to read across a desk; `fields` are the
 * remaining evidence, small and dense. Everything is sized to fit one screen at the modal's
 * `full` size — a record you have to scroll is a record you have to read twice.
 *
 * Presentational only, and a slot for the media: the local app resolves snapshots to URLs while
 * the cloud streams each frame through the node tunnel, and that difference must not fork the
 * layout into two implementations of "event detail".
 */
import { useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import {
  ArrowsRightLeftIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  UserIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';

export type EventFactTone = 'default' | 'good' | 'warn' | 'bad';

export interface EventFact {
  key: string;
  label: string;
  value: ReactNode;
  tone?: EventFactTone;
  /** Ids and plate numbers read better monospaced. */
  mono?: boolean;
  /** The column type it came from — picks the headline tile's icon. */
  kind?: string;
  /**
   * Long free text — what OCR read off a sign, say. Spans the whole grid row and wraps onto as
   * many lines as it needs instead of being cut off at "…": a read the reader cannot see in full
   * is a read they have to open the raw record to check.
   */
  wide?: boolean;
}

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** A headline tile's icon: what KIND of fact it is, recognised before the label is read. */
function factIcon(f: EventFact): Icon | null {
  if (f.key === 'event_timestamp') return ClockIcon;
  switch (f.kind) {
    case 'camera': return VideoCameraIcon;
    case 'face_identity':
    case 'person_identity': return UserIcon;
    case 'inout':
    case 'vehicle_direction': return ArrowsRightLeftIcon;
    case 'plate_number': return IdentificationIcon;
    case 'ppe_violation_type':
    case 'theft_type':
    case 'detection_type': return ExclamationTriangleIcon;
    default: return null;
  }
}

/** "—" is the record saying "not read" — such facts are left out of the record entirely. */
const isBlank = (v: ReactNode) => v === '—' || v === '' || v == null;

/**
 * What the model called this event — "Face Recognized", "Loitering", "No-Helmet" — Title Cased
 * out of whichever field carries it. Empty when the row has none, which the title treats as
 * "the model's own name is the most specific thing we can say".
 */
export function eventTypeLabel(event: any): string {
  const raw: string = event?.sub_type || event?.event_type || event?.violation_type || '';
  return raw ? raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : '';
}

/** The shape of the per-model table column definitions both apps already carry. */
export interface EventColumnLike {
  key: string;
  label: string;
  type?: string;
  render?: (value: any, row?: any) => any;
  /** Show this column's value full-width and wrapped in the record's Details (see EventFact). */
  wide?: boolean;
}

export interface EventValueContext {
  /** camera id → display name. */
  cameraName?: (id: string) => string | undefined;
  /** The registered identity behind a face event, if it has one. */
  personName?: (event: any) => string | null | undefined;
}

const TONE_TEXT: Record<EventFactTone, string> = {
  default: 'text-foreground',
  good: 'text-green-400',
  warn: 'text-amber-400',
  bad: 'text-red-400',
};

const TONE_BADGE: Record<EventFactTone, string> = {
  default: 'bg-muted text-foreground',
  good: 'bg-green-500/15 text-green-500',
  warn: 'bg-amber-500/15 text-amber-500',
  bad: 'bg-red-500/15 text-red-500',
};

function Badge({ text, tone = 'default' }: { text: string; tone?: EventFactTone }) {
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-bold uppercase ${TONE_BADGE[tone]}`}>
      {text}
    </span>
  );
}

/**
 * Render ONE column's value for the detail view, with the tone that goes with it.
 *
 * Shares the table's vocabulary (`col.type`) so a value can't say one thing in the row and
 * another in the record — but not the table's sizing: a badge that has to fit a 13px cell is
 * free to be bigger here.
 */
export function renderEventValue(
  col: EventColumnLike,
  event: any,
  ctx: EventValueContext = {},
): { node: ReactNode; tone: EventFactTone; mono?: boolean } {
  const val = event?.[col.key];
  const plain = (node: ReactNode, tone: EventFactTone = 'default', mono?: boolean) => ({ node, tone, mono });

  switch (col.type) {
    case 'camera':
      return plain(ctx.cameraName?.(val) || (typeof val === 'string' ? val.slice(0, 8) : '') || '—');

    case 'face_identity': {
      /**
       * The resolver first, then the COLUMN'S OWN VALUE.
       *
       * That fallback is not belt-and-braces, it is the fix for a false negative. This case
       * used to read the resolver and nothing else, so any caller that did not supply one
       * reported every matched face as "Unknown" — the Search page's record modal did exactly
       * that, and a search FOR somebody by name opened a record denying it was them. The
       * column is `matched_person_name`; when the record already carries the name, no resolver
       * is needed to read it.
       *
       * The resolver still wins where there is one: it can reach the people registry for an
       * event that stored only a `person_id`, which this value cannot.
       */
      const name = ctx.personName?.(event) || (typeof val === 'string' && val ? val : null);
      return name ? plain(name, 'good') : plain('Unknown', 'warn');
    }
    case 'person_identity':
      return val ? plain(val, 'good') : plain('—');

    case 'face_status':
      return val ? plain(<Badge text="Known" tone="good" />, 'good') : plain(<Badge text="Unknown" tone="warn" />, 'warn');

    case 'inout':
      if (val === 'in') return plain(<Badge text="↓ In" tone="good" />, 'good');
      if (val === 'out') return plain(<Badge text="↑ Out" tone="bad" />, 'bad');
      return plain('—');

    case 'vehicle_direction':
      return val === 'entry'
        ? plain(<Badge text="↓ Entry" tone="good" />, 'good')
        : plain(<Badge text="↑ Exit" tone="warn" />, 'warn');

    case 'person_id': {
      // The registered identity's id, not the AI's per-track detection id, which stops
      // meaning anything once the face is registered.
      const raw = event?.matched_person_id || val;
      return raw ? plain(String(raw), 'default', true) : plain('—');
    }

    case 'plate_number':
      return val ? plain(String(val).toUpperCase(), 'default', true) : plain('—');

    case 'plate_status':
      if (!event?.has_anpr) return plain('No ANPR');
      return plain(
        <Badge text={val || 'detected'} tone={val === 'valid' ? 'good' : val === 'partial' ? 'warn' : 'bad'} />,
        val === 'valid' ? 'good' : val === 'partial' ? 'warn' : 'bad',
      );

    case 'ppe_violation_type': {
      const tone: EventFactTone = String(val || '').toLowerCase().includes('no-') ? 'bad' : 'warn';
      return plain(<Badge text={val || '—'} tone={tone} />, tone);
    }

    case 'theft_type': {
      const tone: EventFactTone = String(val || '').toLowerCase().includes('shoplifting') ? 'bad' : 'warn';
      return plain(<Badge text={val || '—'} tone={tone} />, tone);
    }

    case 'detection_type': {
      // Fire and smoke are one detector's two classes, and they are not the same
      // news: flame is 'bad', smoke is the warning that precedes it. A detector
      // that could not tell which it saw says so rather than guessing.
      const kind = String(val || '').toLowerCase();
      const tone: EventFactTone = kind === 'fire' ? 'bad' : 'warn';
      return plain(<Badge text={val || 'unknown'} tone={tone} />, tone);
    }

    default:
      if (col.render) return plain(col.render(val, event) ?? '—');
      return plain(val == null || val === '' ? '—' : String(val));
  }
}

/**
 * Which columns earn a headline tile, in the order they're read: when it happened, who it was,
 * which way they went, what the model called it, and where. Everything else is evidence.
 *
 * Keyed off the table's own column vocabulary rather than a per-model list, so a model gaining
 * a column gets the right treatment without another place to edit.
 */
function headlineRank(col: EventColumnLike): number {
  if (col.key === 'event_timestamp') return 0;
  switch (col.type) {
    case 'face_identity':
    case 'person_identity':
      return 1;
    case 'inout':
    case 'vehicle_direction':
      return 2;
    case 'plate_number':
    case 'ppe_violation_type':
    case 'theft_type':
    // Fire or smoke is the first thing read off a fire event and decides what the
    // operator does next, so it earns a headline tile rather than a line in the
    // supporting fields.
    case 'detection_type':
      return 3;
    case 'camera':
      return 4;
    default:
      return -1;
  }
}

/** Columns that are the table's own furniture rather than facts about the event. */
const NON_FACT_TYPES = ['images', 'face_action'];

/**
 * Split a model's columns into the headline facts and the rest, both already rendered.
 * One implementation for the modal and the routed page, in both apps.
 */
export function splitEventFacts(
  event: any,
  columns: EventColumnLike[],
  ctx: EventValueContext = {},
): { facts: EventFact[]; fields: EventFact[] } {
  const facts: (EventFact & { rank: number })[] = [];
  const fields: EventFact[] = [];

  for (const col of columns) {
    if (col.type && NON_FACT_TYPES.includes(col.type)) continue;
    const { node, tone, mono } = renderEventValue(col, event, ctx);
    const rank = headlineRank(col);
    if (rank >= 0) facts.push({ key: col.key, label: col.label, value: node, tone, mono, kind: col.type, rank });
    else fields.push({ key: col.key, label: col.label, value: node, tone, mono, kind: col.type, wide: col.wide });
  }

  facts.sort((a, b) => a.rank - b.rank);
  return { facts: facts.map(({ rank, ...f }) => f), fields };
}

export interface EventDetailProps {
  /** The snapshot viewer — each app builds its own, because each loads images differently. */
  media?: ReactNode;
  /**
   * One line under the snapshots. Used to say what the arrow keys do where BOTH the record and
   * the snapshots respond to them — a contract nobody would guess from looking.
   */
  hint?: ReactNode;
  /** Shown large: the few facts the operator acts on. */
  facts: EventFact[];
  /** Shown small and dense: the supporting evidence. */
  fields: EventFact[];
  /**
   * WHOSE APPEARANCE THE PICTURE IS SHOWING — its own titled block, above the event's fields.
   *
   * Appearance is a property of a SUBJECT, and the frame beside it shows one of them; the fields
   * below describe the EVENT, which is a different scope with different answers. They were one
   * grid, so a record of a ten-person frame printed one person's shirt colour under a picture of
   * whoever the reader had clicked to — usually somebody else. Separating them is what lets both
   * be true at once, and the block names who it is about.
   */
  subjectPanel?: ReactNode;
  /**
   * A block below the dense fields, for what neither a tile nor a label/value pair can hold.
   *
   * In practice: the frame's subject breakdown — one row per person or vehicle the event saw.
   * It is a LIST of several things each with several attributes, so it cannot be a `field`
   * (a two-column label/value grid) without being flattened into a sentence nobody can scan.
   */
  extra?: ReactNode;
  /** Buttons that act on the event (register this face, …). */
  actions?: ReactNode;
  /**
   * A standing statement about the record rather than a fact of it — "matched against the
   * registered face vector set". Sits in the footer bar opposite the actions, where it says
   * how the record was arrived at without competing with the facts for the reader's eye.
   */
  note?: ReactNode;
  className?: string;
}

/**
 * One section heading, so every block in the record is introduced the same way.
 *
 * The record grew three blocks that each labelled themselves — "Details", "Appearance", "In
 * this frame" — in three slightly different ways, and a reader scanning down a column reads
 * those differences as meaning something. They mean nothing; they were three authors.
 */
export function SectionHeading({ children, chip, aside, icon: HeadIcon, className = 'mb-2.5' }: {
  children: ReactNode;
  /** A small icon before the title — the block's kind, at a glance. */
  icon?: Icon;
  className?: string;
  /** The block's own answer, when it has a short one — "7 people", "#3 · A. Rao". */
  chip?: ReactNode;
  /** A quiet note on the right: what the block is about, or what to do with it. */
  aside?: ReactNode;
}) {
  return (
    <h3 className={`flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground ${className}`}>
      {HeadIcon && <HeadIcon className="h-4 w-4 shrink-0 text-brand" />}
      <span className="text-foreground">{children}</span>
      {chip && (
        <span className="rounded-md bg-brand-light px-1.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-brand">
          {chip}
        </span>
      )}
      {aside && (
        <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground">
          {aside}
        </span>
      )}
    </h3>
  );
}

/** Copy an id without selecting a truncated string by hand. */
function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          window.setTimeout(() => setDone(false), 1200);
        }).catch(() => { /* insecure context — the value is still in the title */ });
      }}
      title={done ? 'Copied' : 'Copy'}
      aria-label={`Copy ${text}`}
      className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {done ? <CheckIcon className="h-3.5 w-3.5 text-green-500" /> : <ClipboardDocumentIcon className="h-3.5 w-3.5" />}
    </button>
  );
}

/**
 * A label/value grid — the shape both the record's own fields and a subject's read in.
 *
 * Each pair sits on a small tile of its own: as bare text in two columns the pairs ran together
 * and the eye could not tell a label from the value above it. `tile` is the tile surface, which
 * differs by the panel it sits on (card on the tinted Appearance panel, muted on a card).
 */
function FactGrid({ items, tile = 'bg-muted' }: { items: EventFact[]; tile?: string }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((f) => {
        const blank = isBlank(f.value);
        const copyable = f.mono && typeof f.value === 'string' && !blank;
        return (
          <div key={f.key} className={`flex min-w-0 flex-col gap-1 rounded-lg px-3 py-2 ${tile} ${f.wide ? 'col-span-2' : ''}`}>
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {f.label}
            </span>
            <span className="flex min-w-0 items-center gap-1">
              <span
                className={`min-w-0 ${f.wide ? 'whitespace-pre-line break-words' : 'truncate'} text-[14px] font-medium ${
                  blank ? 'text-muted-foreground' : TONE_TEXT[f.tone || 'default']
                } ${f.mono ? 'font-mono text-[13px]' : ''}`}
                title={typeof f.value === 'string' ? f.value : undefined}
              >
                {f.value}
              </span>
              {copyable && <CopyButton text={f.value as string} />}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * WHOSE APPEARANCE THE PICTURE IS SHOWING — the block that goes in `subjectPanel`.
 *
 * Shared rather than written out in each app because both build the identical thing from the
 * identical parts, and two copies of a block whose whole job is to agree with the picture beside
 * it is two chances to disagree with it.
 *
 * BRAND-TINTED, and that is the point rather than decoration: the selected entry in the picker,
 * the chip over the frame and this panel are one claim said three times — "this is the crop you
 * are looking at, and this is who it is". A reader who cannot pair them has to work out which of
 * seven people the words are about from the picture alone.
 */
export function AppearancePanel({ facts, subjectSeq, subjectLabel, ofFrame }: {
  facts: EventFact[];
  /** The module's own index for the subject — the number their crop is named after. */
  subjectSeq?: number | null;
  /** Their resolved identity, where there is one: a name, or a plate. */
  subjectLabel?: string | null;
  /**
   * True when these are the attributes of the crop ON SCREEN, false when they are the subject
   * the record was OPENED from — which is the honest answer under a wide shot, where no one
   * person is being shown. The reader is told which, because they are different claims.
   */
  ofFrame: boolean;
}) {
  // Only what was read: a grid of "—" says nothing, and a panel of only that is not shown.
  facts = facts.filter((f) => !isBlank(f.value));
  if (!facts.length) return null;
  return (
    /* The brand tint moved into a header band with a matching left edge: a whole panel of it
       was loud enough to out-shout the picture it describes, and the pairing with the selected
       crop only needs the colour, not a wall of it. */
    <section className="overflow-hidden rounded-xl border border-border border-l-4 border-l-brand bg-card shadow-sm">
      <div className="border-b border-border bg-brand-light px-3.5 py-2.5">
        <SectionHeading
          icon={UserIcon}
          className=""
          chip={subjectSeq != null
            ? `#${subjectSeq}${subjectLabel ? ` · ${subjectLabel}` : ''}`
            : undefined}
          aside={ofFrame ? 'the crop shown on the left' : 'the subject this record was opened from'}
        >
          Appearance
        </SectionHeading>
      </div>
      <div className="p-3">
        <FactGrid items={facts} />
      </div>
    </section>
  );
}

/** Arrow characters in a hint drawn as keycaps, so "← →" reads as keys to press, not prose. */
function withKeys(text: string): ReactNode[] {
  return text.split(/([←→↑↓])/).map((part, i) => (
    /^[←→↑↓]$/.test(part)
      ? (
        <kbd
          key={i}
          className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-border bg-card px-1 font-sans text-[11px] font-semibold text-foreground shadow-sm"
        >
          {part}
        </kbd>
      )
      : <span key={i}>{part}</span>
  ));
}

export default function EventDetail({
  media, hint, facts, fields, subjectPanel, extra, actions, note, className = '',
}: EventDetailProps) {
  // A fact with no value is not shown at all — "In/Out —" and "People —" were tiles of nothing,
  // and they pushed the facts that DO have an answer further from the eye.
  facts = facts.filter((f) => !isBlank(f.value));
  fields = fields.filter((f) => !isBlank(f.value));
  return (
    <div className={`flex min-h-full flex-col ${className}`}>
      <div className="grid flex-1 gap-6 p-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        {/* Snapshots. First in the DOM as well as on screen: on a narrow window the columns
            stack, and the image is still what the record is about. */}
        {media && (
          <div className="min-w-0">
            {media}
            {/* The hint is a HELP note, so it is drawn as one. As bare grey text under a picture
                it read as a caption — something the record was telling you about this event —
                and the two sentences it actually contains are about the keyboard. */}
            {hint && (
              <p className="mt-2.5 flex flex-wrap items-center gap-x-1 gap-y-1 rounded-lg border border-border bg-muted px-3 py-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {typeof hint === 'string' ? withKeys(hint) : hint}
              </p>
            )}
          </div>
        )}

        <div className="flex min-w-0 flex-col gap-5">
          {/* The headline facts, as tiles.
              `bg-muted`, NOT `bg-muted/60`: the palette tokens are bare `var()` values and
              Tailwind v3 drops the opacity modifier on those SILENTLY — the class was emitted
              into the markup and no rule was ever generated for it, so the tiles this comment
              used to describe as "filled rather than outlined" were neither. They were bare
              text on the card, at two columns, with nothing to say where one fact ended and the
              next began. The hairline border is what keeps them tiles on a theme whose muted
              surface sits close to its card. */}
          {facts.length > 0 && (
            <div className="grid grid-cols-2 gap-2.5">
              {facts.map((f) => {
                const FactIcon = factIcon(f);
                return (
                  <div key={f.key} className="flex min-w-0 items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 shadow-sm">
                    {FactIcon && (
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
                        <FactIcon className="h-[18px] w-[18px]" />
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{f.label}</div>
                      <div
                        className={`mt-0.5 text-[15px] font-semibold leading-tight ${TONE_TEXT[f.tone || 'default']} ${
                          f.mono ? 'break-all font-mono text-[13px]' : ''
                        }`}
                      >
                        {f.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Whose appearance the frame is showing — above the event's own fields, because it
              is about the picture the reader is looking at rather than about the record. */}
          {subjectPanel}

          {/* The supporting evidence, on a surface of its own. Loose on the card it ran into
              whatever came before it, and the eye had only a small grey word to tell it that
              the record had changed subject. */}
          {fields.length > 0 && (
            <section className="rounded-xl border border-border bg-card p-3.5 shadow-sm">
              <SectionHeading icon={DocumentTextIcon} aside="everything else this record carries">Details</SectionHeading>
              <FactGrid items={fields} />
            </section>
          )}
        </div>
      </div>

      {/* FULL WIDTH, below both columns — not inside the right one.
          The subject breakdown is a row of faces with a labelled grid beside each, and the
          right column is the narrow one. Squeezed in there the thumbnails and the attributes
          fight for the same 300px and neither is readable; across the whole record it is two
          cards wide and scannable. It also belongs last on its own merits: it describes the
          CONTEXT the record sits in — how many people were there, and who else — which is the
          question that follows reading the record rather than one competing with it. */}
      {extra && <div className="px-5 pb-5">{extra}</div>}

      {/* One bar across the whole record, not a strip inside the right-hand column: what acts on
          the event acts on all of it, and stuck at the bottom of the scroll it stays reachable
          in a record that has been scrolled past its own actions. */}
      {(actions || note) && (
        /* `bg-card`, NOT `bg-card/95`: the same Tailwind v3 trap as the fact tiles above. The
           bar was TRANSPARENT, so in a record long enough to scroll — which is every record on
           a short window — the fields slid underneath the actions and the note, and the one
           thing that must stay readable was the one thing with a record printed through it. */
        <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border bg-card px-5 py-3">
          <p className="min-w-0 text-[12px] text-muted-foreground">{note}</p>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
    </div>
  );
}
